import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { PERFORMANCE_BY_PRODUCT_QUERY } from "./query";
/**
 * A metric is only meaningful when the backend returns a finite number. Anything
 * else — the field missing, or a non-numeric marker such as "implementation
 * pending" — becomes null so the UI can show "no data" rather than a misleading
 * 0 (and so a marker can never leak through the numeric contract).
 */
const toMetric = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
/**
 * Typed handler for the Overview dashboard Performance by Product widget. Owns
 * the LookerML query construction, response typing, and caching so the frontend
 * hook never builds a raw query envelope (see the OA-131 review feedback that
 * required all overview fetches to go through typed SDK handlers).
 */
export default class LookerOverviewPerformanceByProduct extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-performance-by-product");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...PERFORMANCE_BY_PRODUCT_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            queryBody.filters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: product performance is partner-scoped exclusively by
        // advertiser + campaign. Without both, the query would aggregate across
        // every partner, so we refuse to run it (matches the sibling top-line /
        // placements handlers). This only rejects missing ids — it does not verify
        // the caller owns the ids supplied.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["advertisers.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["campaigns.campaign_id"] = campaignIds.join(",");
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard performance by product");
        }));
    }
    formatResponse(data) {
        return data
            .filter((row) => row["explore_overview.product_group"])
            .map((row) => ({
            productGroup: row["explore_overview.product_group"],
            impressions: toMetric(row["explore_overview.total_impressions"]),
            completes: toMetric(row["explore_overview.total_video_completes"]),
            reach: toMetric(row["explore_overview.total_reach"]),
            frequency: toMetric(row["explore_overview.avg_frequency"]),
            spend: toMetric(row["explore_overview.total_cost"]),
            cpa: toMetric(row["explore_overview.cpa"]),
            conversions: toMetric(row["explore_overview.total_conversions"]),
            roas: toMetric(row["explore_overview.roas"])
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=PerformanceByProduct.js.map